import React, { useEffect, useState, useCallback } from 'react'
import async from 'async-es'
import debounce from 'lodash.debounce'

import { Switch, Divider, BarLoader } from '@st-operations/uikit/src/v2/index.js'
import convertToArray from '@st-operations/uikit/lib/convertToArray.js'
import pretty from '@st-operations/uikit/lib/pretty.js'
import format from '@st-operations/uikit/lib/format.js'
import Events from '@st-operations/uikit/Events'

import Select from '_components/select/index.jsx'

import searchEntities from '_lib/searchEntities.js'
import { logErrorAsync } from '_lib/utility.js'

import DraggableList from './DraggableList.jsx'
import TimeIntervalOptions from './TimeIntervalOptions.jsx'

import { EDIT_FIELDS } from './constants/availableSlides.js'

import './SelectedEntitiesEditor.scss'
import { SELECT_ACTIONS } from '../select/constants.js'

const IS_OVERINDEX = [
  'L2_Categories_and_L3_Categories', // passion points
  'L2_and_L3_Categories_WordCloud', // category cloud
  'Recommended_Segments', // audiences to activate
  'Seasonality_by_Category', // recent trends by category
]

const parseEventName = (name) => {
  let words = name.split('_')
  let parsed_name = ''
  words.forEach((word) => {
    parsed_name = `${parsed_name}${word[0].toUpperCase()}${word.slice(1)} `
  })
  return parsed_name.trim()
}

// shorten label if too long
const formatName = (name) => {
  const prettifiedName = pretty(name)

  if (prettifiedName.length > 60) {
    return `${prettifiedName.slice(0, 30)}...${prettifiedName.slice(prettifiedName.length - 30)}`
  }
  return prettifiedName
}

// for Audience Trend
const ManageEventsSwitches = ({ selected_events, onToggleEvent }) => (
  <>
    <p className="selected-entities-editor__title">Manage events:</p>
    <For each="event" index="index" of={Object.keys(selected_events)}>
      <div className="selected-entities-editor__manage-events" key={index}>
        <span>{parseEventName(event)}</span>
        <Switch checked={selected_events[event]} name={event} onChange={onToggleEvent(event)} />
      </div>
    </For>
    <Divider className="selected-entities-editor__divider" />
  </>
)

// component
const SelectedEntitiesEditor = ({
  field,
  slide,
  categoryOverindex,
  index,
  onFieldChange,
  constraint,
}) => {
  const { data, template, options } = slide
  const [state, setState] = useState({
    entity_options: [],
    categories: [],
    loading: false,
    query: '',
  })
  const isAudience = data.section_1.type === 'Segments'
  const isCategory = IS_OVERINDEX.includes(template)

  const selectedEntities = data?.selected_entities ?? data?.[field]?.selected_entities ?? []

  // Handle updates on selected entities' drag list
  useEffect(() => {
    // setListOrder is the event emitted by DraggableList
    const event = Events.addListener('setListOrder', (newData) => {
      if (newData?.field && newData?.list) {
        // save data in global object to sent to BE when saving
        onFieldChange({
          index: newData.index,
          value: newData.list,
          type: 'selected_entities',
          field: newData.field,
        })
      }
    })

    return () => event.remove()
  }, [])

  const loadCategories = () => {
    const categoriesObj = {
      ...(field === EDIT_FIELDS.RECOMMENDED_SEGMENTS_1 ? categoryOverindex?.L1 ?? {} : {}),
      ...(categoryOverindex?.L2 ?? {}),
      ...(categoryOverindex?.L3 ?? {}),
    }

    const categories = convertToArray(categoriesObj, 'category', '').map((cat) => ({
      label: `${cat.key} (${format('overindex', cat.value)})`,
      value: { ...cat, name: format('category', cat.original_key, null, true) },
    }))

    setState((prevState) => ({ ...prevState, entity_options: categories, categories }))
  }

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      entity_options: [],
    }))
    if (IS_OVERINDEX.includes(template) && categoryOverindex) {
      loadCategories()
    }
  }, [index, template, categoryOverindex])

  const onSelectChange = (item, { action }) => {
    if (action === SELECT_ACTIONS.SELECT_OPTION) {
      const { value } = item
      const newSelectedEntities = [...selectedEntities, value]
      onFieldChange({ index, value: newSelectedEntities, type: 'selected_entities', field })

      setState((prevState) => ({
        ...prevState,
        query: '',
        loading: false,
      }))
    }
  }

  const searchAudiences = (query, { action }) => {
    if (isAudience && action === SELECT_ACTIONS.INPUT_CHANGE) {
      const options = [
        {
          kind: 'custom',
          limit: 10,
          query,
          status: 'draft,approved,processed',
        },
        {
          kind: 'taxonomy',
          limit: 10,
          query,
          status: 'draft,approved,processed',
        },
      ]
      debouncedSearchAudience(query, 'audiences', options)
    }
  }

  const debouncedSearchAudience = useCallback(
    debounce(async (query, collection, options) => {
      if (!query) {
        return
      }

      try {
        setState((prevState) => ({ ...prevState, loading: true, entity_options: [] }))

        // ensure options are an array for the async module
        if (!Array.isArray(options)) {
          options = [options]
        }

        let entity_options = []
        async.each(
          options,
          (_options, next) => {
            searchEntities(collection, { ..._options }, (entities) => {
              entities.forEach((entity) => {
                entity_options.push({
                  label: formatName(entity.name),
                  value: entity,
                })
              })
              next()
            })
          },
          (error) => {
            if (error) {
            } else {
              if (entity_options?.[0]?.value?.updated_at) {
                entity_options.sort((a, b) => {
                  return a?.value?.updated_at > b?.value?.updated_at ? -1 : 1
                })
              }
              setState((prevState) => ({
                ...prevState,
                entity_options,
                query: '',
                loading: false,
              }))
            }
          }
        )
      } catch (error) {
        setState((prevState) => ({
          ...prevState,
          loading: false,
          query: '',
        }))
        logErrorAsync(error)
        toast.error(error.message)
      }
    }, 1000),
    []
  )

  const onToggleEvent = (name) => (event) => {
    const { checked } = event.target
    let selected_events = { ...data.selected_events }
    selected_events[name] = checked
    onFieldChange({ index, value: selected_events, type: 'selected_events', field })
  }

  const removeEntity = ({ currentTarget }) => {
    const value = JSON.parse(currentTarget.dataset.value)
    const selected_entities = selectedEntities.filter((item) => {
      if (isAudience) return item._id !== value._id
      if (isCategory) return item.key !== value.key
    })
    onFieldChange({ index, value: selected_entities, type: 'selected_entities', field })
  }

  // modify select labels
  const itemType = data[field]?.type ?? data['section_1']?.type

  const filterOptions = (options, selected) => {
    if (isAudience) {
      const keys = selected.map(({ _id }) => _id)
      return options.map((item) =>
        keys.includes(item.value._id) ? { ...item, isDisabled: true } : item
      )
    } else if (isCategory) {
      const keys = selected.map(({ original_key, originalKey }) => original_key ?? originalKey)
      return options.map((item) =>
        keys.includes(item.value.original_key ?? item.value.originalKey)
          ? { ...item, isDisabled: true }
          : item
      )
    }
  }

  const noOptionsMessage = () => {
    if (isAudience) {
      return state.loading ? 'Loading...' : 'Type to search'
    }
    if (isCategory) {
      return !categoryOverindex && 'Loading...'
    }
    return null
  }

  return (
    <div className="selected-entities-editor">
      <Divider className="selected-entities-editor__divider" />
      <TimeIntervalOptions
        index={index}
        timeIntervalOptions={options}
        onFieldChange={onFieldChange}
        timeRange={data.time_range}
        timeFrame={data.time_frame}
        yLabels={data.y_labels}
      />
      <If condition={data.selected_events}>
        <ManageEventsSwitches
          selected_events={data.selected_events}
          onToggleEvent={onToggleEvent}
        />
      </If>
      <If condition={constraint && constraint.min && constraint.max}>
        <h5>{`Note: You need to add at least ${constraint.min}
              and no more than ${constraint.max} ${itemType.toLowerCase()}`}</h5>
      </If>
      <If condition={!data.hide_select}>
        <Select
          label={`Select ${itemType} to add to your chart:`}
          placeholder="eg: ShareThis, Facebook"
          options={filterOptions(state.entity_options, selectedEntities)}
          onChange={onSelectChange}
          onInputChange={searchAudiences}
          isDisabled={selectedEntities?.length >= constraint?.max}
          isSearchable={true}
          noOptionsMessage={noOptionsMessage}
        />
        <If condition={state.loading}>
          <BarLoader containerStyle={{ left: 0, position: 'relative' }} />
        </If>
        <div style={{ margin: '16px 0 20px' }}>
          <DraggableList
            template={template}
            label={`${itemType} Order:`}
            description="Drag and drop items to change order"
            slideIndex={index}
            format={(data.insights && data.insights[0][1]) || 'percent'}
            onRemove={removeEntity}
            shown={[...selectedEntities]}
            shown_field={field ?? 'selected_entities'}
          />
        </div>
      </If>
    </div>
  )
}
// export
export default SelectedEntitiesEditor
